import { useCallback, useEffect, useState } from "react"

import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { Button, Modal, Segment } from "semantic-ui-react"
import styled from "styled-components"

import { ImageChooser } from "components/lib/Choosers"

const imageHeight = 250
const fullImageHeight = 365

const MultiImageSegment = styled(Segment)`
  margin: 20px 0 20px 0;
  display: flex;
  align-items: center;
  justify-content: "space-evenly";
  flex-wrap: wrap;
  flex-direction: ${({ theme }) => (theme.sizes.isMobile ? "column" : "row")};
  &:hover {
    ${({ $dragActive }) => $dragActive && "background: #c0c0c0c !important;"}
  }
`

const MultiImageContainer = styled.div`
  padding: 0px 0 20px 0;
  display: flex;
  align-items: center;
  justify-content: "space-evenly";
  flex-wrap: wrap;
  flex-direction: ${({ theme }) => (theme.sizes.isMobile ? "column" : "row")};
`

const GalleryContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 500;
`

const GalleryImages = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  flex: 1;
`

const GalleryImage = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 10px;
  margin: 3px;
  object-fit: cover;
  ${({ theme, $active }) => $active && `border: 3px solid ${theme.primaryColour}`};
  cursor: pointer;
`

const NewMultiImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-left: 10px;
  flex: 0.5;
`

const NoActiveImage = styled.div`
  border-radius: 10px;
  height: ${({ $imageHeight }) => $imageHeight};
  width: ${({ $imageWidth }) => $imageWidth};
  border: 1px solid lightgray;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 3px;
`

const MultiImageChooser = ({
  images = [],
  onComplete,
  deleteFile,
  removeAsImage,
  forType,
  forId,
  trigger,
  title,
  description,
  editing,
  getFileUrl,
  useSegment,
  maxImages = 99,
}) => {
  const [activeImage, setActiveImage] = useState(null)
  const { t } = useTranslation()
  const [imageModalOpen, setImageModalOpen] = useState(false)

  const deleteImage = (s) => {
    setActiveImage(null)
    deleteFile(s._id)
  }

  const toggleOffImage = (s) => {
    setActiveImage(null)
    removeAsImage(s)
  }

  useEffect(() => {
    if (images) {
      setActiveImage(images[0])
    } else setActiveImage(null)
  }, [images])

  const handleEnterPress = useCallback((event) => {
    if (event.keyCode === 13 && editing) {
      setImageModalOpen(true)
    }
  })

  const sharedProps = {
    forType,
    forId,
    onError: (e) => toast.error(e),
  }

  const Wrapper = useSegment ? MultiImageSegment : MultiImageContainer
  if (!editing && !images.length) return null
  return (
    <Wrapper>
      <GalleryContainer>
        {activeImage ? (
          <div style={{ margin: 3, flex: 1 }}>
            <div
              style={{
                display: "flex",
                flex: 1,
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 10,
              }}
            ></div>
            <div
              style={{
                display: "flex",
                backgroundColor: "#f8f8f8",
                borderRadius: 15,
              }}
            >
              <img
                tabIndex={0}
                src={getFileUrl ? getFileUrl(activeImage) : activeImage.url}
                alt={getFileUrl ? getFileUrl(activeImage) : activeImage.url}
                onClick={() => {
                  setImageModalOpen(true)
                }}
                onKeyDown={(e) => handleEnterPress(e)}
                style={{
                  height: editing ? imageHeight : fullImageHeight,
                  width: "100%",
                  objectFit: "contain",
                  borderRadius: 10,
                  cursor: editing ? "pointer" : "default",
                }}
              />
            </div>
          </div>
        ) : (
          <NoActiveImage $imageHeight={`${imageHeight + 40}px`} $imageWidth={"100%"}>
            <h5>{!images.length ? t("multiimage.upload.none") : "Select an image"}</h5>
          </NoActiveImage>
        )}
        <GalleryImages>
          {images && Array.isArray(images) && images.length
            ? images.map((s) => (
                <GalleryImage
                  src={getFileUrl ? getFileUrl(s) : s.url}
                  key={s._id}
                  onClick={() => setActiveImage(s)}
                  $active={activeImage?._id === s._id}
                />
              ))
            : null}
        </GalleryImages>
      </GalleryContainer>
      {editing ? (
        <NewMultiImageContainer>
          <h5 style={{ margin: 5 }}>{title}</h5>
          <p style={{ textAlign: "center", marginBottom: 10 }}>{description}</p>
          <ImageChooser position={"bottom right"} trigger={trigger} onComplete={onComplete} {...sharedProps} />
          {maxImages ? (
            <p style={{ marginTop: 10 }}>
              <b>
                {images.length} / {maxImages} maximum images uploaded
              </b>
            </p>
          ) : null}
          <div style={{ display: "flex" }}>
            {editing ? (
              <>
                {removeAsImage ? (
                  <Button
                    size="mini"
                    basic
                    content="Remove as stock image"
                    icon="ban"
                    onClick={() => toggleOffImage(activeImage)}
                  />
                ) : null}
                <Button
                  size="mini"
                  basic
                  content="Remove selected image"
                  icon="trash"
                  onClick={() => deleteImage(activeImage)}
                />
              </>
            ) : null}
          </div>
        </NewMultiImageContainer>
      ) : null}
      {activeImage && (
        <Modal
          open={imageModalOpen}
          onClose={() => setImageModalOpen(false)}
          style={{ maxWidth: "90vw", maxHeight: "90vh" }}
        >
          <Modal.Header> {activeImage.name} </Modal.Header>
          <Modal.Content
            scrolling
            style={{
              display: "flex",
              justifyContent: "center",
              padding: 0,
              borderRadius: "10px",
              overflow: "hidden",
            }}
          >
            {activeImage && (
              <img
                src={getFileUrl ? getFileUrl(activeImage) : activeImage.url}
                alt={getFileUrl ? getFileUrl(activeImage) : activeImage.url}
                style={{
                  maxWidth: "90%",
                  maxHeight: "60%",
                  objectFit: "contain",
                  marginBottom: 10,
                  marginTop: 10,
                }}
              />
            )}
          </Modal.Content>
          <Modal.Actions>
            {activeImage && (
              <a
                href={getFileUrl ? getFileUrl(activeImage) : activeImage.url}
                download={activeImage.name || "image.png"}
              >
                <Button primary content="Download image" icon="download" />
              </a>
            )}
            <Button
              content="Close"
              onClick={() => {
                setImageModalOpen(false)
              }}
            />
          </Modal.Actions>
        </Modal>
      )}
    </Wrapper>
  )
}

export default MultiImageChooser
